import { Suspense, lazy } from "react";
import { Navigate, Outlet, Route, Routes, useLocation } from "react-router-dom";
import AppShell from "./layout/AppShell";
import ErrorBoundary from "./components/ErrorBoundary";
import { useAuth } from "./context/AuthContext";
import { entradaDeRuta } from "./layout/navigation";
import { EmptyState, Spinner } from "./components/ui";
import LoginPage from "./modules/auth/LoginPage";

/* =========================================================================
   RUTAS
   Todo el panel vive detrás de RequireAuth. Las rutas van en español porque
   son visibles en la barra de direcciones y forman parte de la interfaz.

   Cada módulo se carga bajo demanda: sin esto todo Recharts entraba en el
   paquete inicial y el login —que no dibuja una sola gráfica— arrastraba
   800 kB antes de pintar.
   ======================================================================== */

const DashboardPage = lazy(() => import("./modules/dashboard/DashboardPage"));
const OrdersPage = lazy(() => import("./modules/orders/OrdersPage"));
const DomiciliariesPage = lazy(
  () => import("./modules/domiciliaries/DomiciliariesPage"),
);
const PaymentsPage = lazy(() => import("./modules/payments/PaymentsPage"));
const ComprobantesPage = lazy(
  () => import("./modules/facturas/ComprobantesPage"),
);
const BusinessesPage = lazy(
  () => import("./modules/businesses/BusinessesPage"),
);
const ProductsPage = lazy(() => import("./modules/products/ProductsPage"));
const CategoriesPage = lazy(
  () => import("./modules/categories/CategoriesPage"),
);
const UsersPage = lazy(() => import("./modules/users/UsersPage"));
const OwnersPage = lazy(() => import("./modules/owners/OwnersPage"));
const ComplexesPage = lazy(() => import("./modules/complexes/ComplexesPage"));
const ReviewsPage = lazy(() => import("./modules/reviews/ReviewsPage"));
const ChatsPage = lazy(() => import("./modules/chats/ChatsPage"));
const MarketingOverviewPage = lazy(
  () => import("./modules/marketing/MarketingOverviewPage"),
);
const AdvertisersPage = lazy(
  () => import("./modules/marketing/AdvertisersPage"),
);
const CampaignsPage = lazy(() => import("./modules/marketing/CampaignsPage"));
const BannersPage = lazy(() => import("./modules/marketing/BannersPage"));
const CouponsPage = lazy(() => import("./modules/marketing/CouponsPage"));
const FeaturedPage = lazy(() => import("./modules/marketing/FeaturedPage"));
const PushPage = lazy(() => import("./modules/marketing/PushPage"));
const AreasPage = lazy(() => import("./modules/areas/AreasPage"));
const DocumentosPage = lazy(() => import("./modules/sst/DocumentosPage"));
const IncidentesPage = lazy(() => import("./modules/sst/IncidentesPage"));
const PqrsPage = lazy(() => import("./modules/calidad/PqrsPage"));
const SolicitudesPage = lazy(() => import("./modules/calidad/SolicitudesPage"));
const EfectivoPage = lazy(() => import("./modules/contabilidad/EfectivoPage"));
const LiquidacionesPage = lazy(
  () => import("./modules/contabilidad/LiquidacionesPage"),
);
const ReportsPage = lazy(() => import("./modules/reports/ReportsPage"));
const AuditPage = lazy(() => import("./modules/audit/AuditPage"));
const FallosPage = lazy(() => import("./modules/fallos/FallosPage"));
const SettingsPage = lazy(() => import("./modules/settings/SettingsPage"));
const MiCuentaPage = lazy(() => import("./modules/settings/MiCuentaPage"));

function Cargando({ texto = "Cargando…" }) {
  return (
    <div style={{ display: "grid", placeItems: "center", minHeight: "50vh" }}>
      <span className="row gap-3 muted">
        <Spinner size={18} /> {texto}
      </span>
    </div>
  );
}

function RequireAuth({ children }) {
  const { isAuthenticated, booting } = useAuth();
  const location = useLocation();

  // Mientras se revalida el token guardado no se decide nada: redirigir acá
  // sacaría al usuario al login en cada recarga.
  if (booting) return <Cargando texto="Verificando sesión…" />;

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  return children;
}

/**
 * Puerta por sección dentro del panel.
 *
 * Ocultar la entrada del menú no basta: la URL se puede escribir a mano y
 * quedaría una pantalla intentando cargar datos que el servidor va a rechazar,
 * llenándola de errores en vez de decir qué pasa. Acá se dice.
 *
 * Es comodidad de la interfaz, no seguridad: el middleware `modulo:` del
 * backend rechaza la petición igual aunque esto se saltara.
 */
function RequireModule({ children }) {
  const { permisos, puedeVer, puedeGestionar } = useAuth();
  const location = useLocation();

  // Permisos todavía en camino: no se decide nada, o se expulsaría a alguien
  // de una sección a la que sí tiene derecho.
  if (permisos === null) return <Cargando texto="Comprobando permisos…" />;

  const entrada = entradaDeRuta(location.pathname);
  const modulo = entrada?.modulo ?? null;

  if (modulo && !puedeVer(modulo)) {
    return (
      <div className="card">
        <EmptyState
          icon="shield"
          title="Esta sección no es de tu área"
          text="Tu área no tiene acceso a esta parte del panel. Si crees que deberías tenerlo, pídeselo a Tecnología."
        />
      </div>
    );
  }

  /*
   * Roles y accesos es la única sección que no se puede ni abrir sin gestión:
   * el listado ES el editor de permisos. Se dice con otras palabras porque es
   * otro problema — el área sí alcanza, lo que falta es el nivel.
   */
  if (modulo && entrada?.exigeGestion && !puedeGestionar(modulo)) {
    return (
      <div className="card">
        <EmptyState
          icon="shield"
          title="Solo consulta"
          text="Esta sección es el gestor de permisos: no tiene una vista de solo lectura. Pídele a Tecnología que te suba de nivel si necesitas entrar."
        />
      </div>
    );
  }

  return children;
}

/**
 * Envoltura común de todas las secciones: permiso, carga diferida y límite de
 * error, en ese orden.
 *
 * El límite se reinicia con la ruta —de ahí `claveReinicio`—: sin eso, una
 * pantalla que falló se quedaría puesta encima de la siguiente, que quizá
 * funciona perfectamente.
 */
function ZonaProtegida() {
  const { pathname } = useLocation();

  return (
    <ErrorBoundary claveReinicio={pathname}>
      <RequireModule>
        <Suspense fallback={<Cargando />}>
          <Outlet />
        </Suspense>
      </RequireModule>
    </ErrorBoundary>
  );
}

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />

      <Route
        element={
          <RequireAuth>
            <AppShell />
          </RequireAuth>
        }
      >
        <Route element={<ZonaProtegida />}>
          <Route index element={<DashboardPage />} />

          <Route path="ordenes" element={<OrdersPage />} />
          {/*
            La ficha abierta tiene URL propia. Es la misma pantalla: el
            parámetro solo dice qué ficha dejar abierta, para que el enlace se
            pueda pegar en un chat y para que recargar no la cierre.
          */}
          <Route path="ordenes/:id" element={<OrdersPage />} />
          <Route path="domiciliarios" element={<DomiciliariesPage />} />
          <Route path="pagos" element={<PaymentsPage />} />
          <Route path="comprobantes" element={<ComprobantesPage />} />
          <Route path="comprobantes/:id" element={<ComprobantesPage />} />
          <Route path="liquidaciones" element={<LiquidacionesPage />} />
          <Route path="efectivo" element={<EfectivoPage />} />

          {/* SST: documentación vigente e incidentes de la operación. */}
          <Route path="sst">
            <Route path="documentos" element={<DocumentosPage />} />
            <Route path="incidentes" element={<IncidentesPage />} />
          </Route>

          <Route path="negocios" element={<BusinessesPage />} />
          <Route path="productos" element={<ProductsPage />} />
          <Route
            path="categorias"
            element={<CategoriesPage scope="product" />}
          />
          <Route
            path="categorias-negocio"
            element={<CategoriesPage scope="business" />}
          />

          {/* Marketing. Rutas anidadas bajo /marketing porque son siete
              pantallas de un mismo dominio: en plano competirían con las de
              catálogo y operación en la barra de direcciones. */}
          <Route path="marketing">
            <Route index element={<MarketingOverviewPage />} />
            <Route path="anunciantes" element={<AdvertisersPage />} />
            <Route path="campanas" element={<CampaignsPage />} />
            <Route path="banners" element={<BannersPage />} />
            <Route path="cupones" element={<CouponsPage />} />
            <Route path="destacados" element={<FeaturedPage />} />
            <Route path="notificaciones" element={<PushPage />} />
          </Route>

          <Route path="usuarios" element={<UsersPage />} />
          <Route path="propietarios" element={<OwnersPage />} />
          <Route path="conjuntos" element={<ComplexesPage />} />
          <Route path="resenas" element={<ReviewsPage />} />
          <Route path="chats" element={<ChatsPage />} />
          <Route path="pqrs" element={<PqrsPage />} />
          <Route path="solicitudes" element={<SolicitudesPage />} />

          <Route path="areas" element={<AreasPage />} />
          <Route path="reportes" element={<ReportsPage />} />
          <Route path="auditoria" element={<AuditPage />} />
          <Route path="fallos" element={<FallosPage />} />
          <Route path="ajustes" element={<SettingsPage />} />
          {/*
            SIN módulo a propósito. `ajustes` solo lo tiene Tecnología, y esta
            pantalla es donde cada persona activa su segundo factor y revisa sus
            sesiones: dejarla ahí dentro habría excluido a doce de las catorce
            cuentas, incluidas las de Contabilidad, que son las que mueven
            dinero.
          */}
          <Route path="mi-cuenta" element={<MiCuentaPage />} />
        </Route>

        <Route
          path="*"
          element={
            <div className="card">
              <EmptyState
                icon="search"
                title="Página no encontrada"
                text="La ruta que buscas no existe en el panel."
              />
            </div>
          }
        />
      </Route>
    </Routes>
  );
}
